import { getPurchaseAmounts } from '../utils/purchaseAmounts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updatePurchaseOrder,
  deletePurchaseOrder,
  updatePurchaseOrderItems,
  verifyReceivedPurchaseOrder,
  fetchPurchaseOrders,
  fetchInventoryProducts,
  fetchStockTransactions,
} from '../features/inventory/stockManagerInventorySlice';

import { receiveVerifiedPurchaseToInventory } from '../features/inventory/inventoryMovementSlice';

import VerifyItemInventoryModal from './VerifyItemInventoryModal';
import PurchaseOrderDocuments from './PurchaseOrderDocuments';
import PurchaseProductSearch from './PurchaseProductSearch';
import { API_BASE_URL } from '../utils/apiConfig';
import { getPurchaseChargeDraft } from '../utils/purchaseChargeDraft';
import './PurchaseOrdersStatusSections.css';
import { downloadPurchaseVerificationCsv, importPurchaseVerificationCsv } from '../utils/purchaseVerificationCsv';

const STATUSES = ['draft', 'sent', 'intransit', 'received', 'verified'];
const MANUAL_STATUS_OPTIONS = ['draft', 'sent', 'intransit', 'received'];

const STATUS_STYLES = {
  draft: {
    section: 'bg-slate-50 border-slate-300',
    header: 'bg-slate-100 text-slate-800',
    badge: 'bg-slate-200 text-slate-800',
  },
  sent: {
    section: 'bg-blue-50 border-blue-300',
    header: 'bg-blue-100 text-blue-800',
    badge: 'bg-blue-200 text-blue-800',
  },
  intransit: {
    section: 'bg-orange-50 border-orange-300',
    header: 'bg-orange-100 text-orange-800',
    badge: 'bg-orange-200 text-orange-800',
  },
  received: {
    section: 'bg-yellow-50 border-yellow-300',
    header: 'bg-yellow-100 text-yellow-800',
    badge: 'bg-yellow-200 text-yellow-800',
  },
  verified: {
    section: 'bg-green-50 border-green-300',
    header: 'bg-green-100 text-green-800',
    badge: 'bg-green-200 text-green-800',
  },
};

const makeBatchId = () => {
  const d = new Date();
  const ymd = d.toISOString().slice(0, 10).replaceAll('-', '');
  const time = String(d.getTime()).slice(-5);
  return Number(`${ymd}${time}`);
};

const getBrandName = (p) =>
  p?.brand_name_english ||
  p?.brand_name_englishh ||
  p?.brand_name_telugu ||
  p?.brand_name ||
  '';

const getProductDisplayName = (item) => {
  const productName = String(item?.product_name || item?.product_id || '').trim();
  const brandName = String(item?.brand_name || item?.brand_name_english || '').trim();

  if (!brandName) return productName;
  if (!productName) return brandName;

  return productName.toLowerCase().startsWith(brandName.toLowerCase())
    ? productName
    : `${brandName} ${productName}`;
};

const makeSkuId = (item) => {
  const code = String(item?.product_code || `MKP${item?.product_id || item?.id || ''}`).replace(/\s+/g, '');
  const exp = item?.exp_date ? String(item.exp_date).replaceAll('-', '') : 'NOEXP';
  return `${code}-${exp}`;
};

// PO created date: use the saved timestamp, else the millisecond timestamp inside the PO number (PO-<ms>-<n>).
const getPoCreatedDate = (po) => {
  const raw = po?.created_at || po?.createdAt || po?.created_on || po?.po_date || po?.order_date;
  let date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) {
    const match = String(po?.po_number || '').match(/(\d{13})/);
    date = match ? new Date(Number(match[1])) : null;
  }
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const toDayKey = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const getPoDateFor = (po, field) => {
  if (field === 'expected') return toDayKey(po?.expected_date);
  if (field === 'received') return toDayKey(po?.arrived_date || po?.received_date);
  const created = getPoCreatedDate(po);
  return created === '-' ? '' : created;
};

const todayKey = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const getItemMrp = (item) => item?.unit_mrp ?? item?.unit_MRP ?? item?.mrp ?? item?.MRP ?? '';

// Order of cells that Tab / Enter move through while verifying.
const VERIFY_EDIT_FIELDS = ['qty', 'no_of_units', 'actual_unit_price', 'unit_mrp', 'mfg_date', 'exp_date', 'sku_id', 'inventory_remarks'];
const VERIFY_NUMERIC_FIELDS = new Set(['qty', 'no_of_units', 'actual_unit_price', 'unit_mrp']);
const VERIFY_RECEIPT_FIELDS = new Set(['qty', 'no_of_units', 'actual_unit_price', 'unit_mrp', 'mfg_date', 'exp_date', 'sku_id']);

const getMissingVerifyFields = (item) => {
  const missing = [];
  if (item.actual_unit_price === '' || item.actual_unit_price === null || item.actual_unit_price === undefined || Number(item.actual_unit_price) < 0) missing.push('actual_unit_price');
  if (!(Number(getItemMrp(item)) > 0)) missing.push('unit_mrp');
  if (!item.exp_date) missing.push('exp_date');
  return missing;
};

const InlineEditCell = ({ label, editing, value, display, type = 'text', className = '', invalid = false, placeholder, min, step, onStart, onDone }) => {
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);
  const finished = useRef(false);

  useEffect(() => {
    if (!editing) return;
    finished.current = false;
    setDraft(value ?? '');
    const id = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      // Focus without scrolling the whole order list sideways; only bring the cell into view.
      el.focus({ preventScroll: true });
      if (type !== 'date') el.select?.();
      const td = el.closest('td');
      const box = el.closest('.purchase-product-details');
      if (td && box) {
        const left = td.offsetLeft;
        const right = left + td.offsetWidth;
        if (left < box.scrollLeft) box.scrollLeft = Math.max(0, left - 40);
        else if (right > box.scrollLeft + box.clientWidth) box.scrollLeft = right - box.clientWidth + 40;
        const rect = td.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) window.scrollBy(0, rect.top - window.innerHeight / 2);
      }
    }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const finish = (save, move) => {
    if (finished.current) return;
    finished.current = true;
    onDone(save, draft, move);
  };

  return (
    <td
      data-label={label}
      title={editing ? undefined : 'Double-click to edit'}
      onDoubleClick={(e) => { e.stopPropagation(); if (!editing) onStart(); }}
      className={`purchase-inline-cell p-2 ${invalid ? 'purchase-inline-missing' : ''} ${className}`}
    >
      {editing ? (
        <input
          ref={inputRef}
          aria-label={label}
          type={type}
          min={min}
          step={step}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={() => finish(true, null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); finish(true, e.shiftKey ? 'up' : 'down'); }
            else if (e.key === 'Escape') { e.preventDefault(); finish(false, null); }
            else if (e.key === 'Tab') { e.preventDefault(); finish(true, e.shiftKey ? 'prev' : 'next'); }
          }}
          className="purchase-inline-input"
        />
      ) : (
        display
      )}
    </td>
  );
};

const PurchaseOrdersStatusSections = ({
  purchaseOrders = [],
  productBarcodes = [],
}) => {
  const dispatch = useDispatch();
  const { brands = [], categories = [], units = [] } = useSelector(state => state.stockManagerInventory || {});
  const token = useSelector((state) => state.posUser?.userInfo?.token || '');

  const [openOrderId, setOpenOrderId] = useState(null);
  const [documentOrderId, setDocumentOrderId] = useState(null);
  const documentOrder = purchaseOrders.find(po => po.id === documentOrderId);
  const [deletingId, setDeletingId] = useState(null);
  const handleDelete = async (po) => {
    if (deletingId !== null || po.status !== 'draft') return;
    if (!window.confirm(`Delete draft purchase order ${po.po_number || po.id} and its items? This cannot be undone.`)) return;
    setDeletingId(po.id);
    try {
      await dispatch(deletePurchaseOrder(po.id)).unwrap();
      if (openOrderId === po.id) setOpenOrderId(null);
      if (editingId === po.id) setEditingId(null);
      if (editingItemsId === po.id) setEditingItemsId(null);
    } catch (error) {
      window.alert(typeof error === 'string' ? error : error.message || 'Unable to delete purchase order');
    } finally { setDeletingId(null); }
  };
  const [editingId, setEditingId] = useState(null);
  const [editingItemsId, setEditingItemsId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [savingVerifyId, setSavingVerifyId] = useState(null);
  const [savingVerifyProgress, setSavingVerifyProgress] = useState('');

  const [form, setForm] = useState({});
  const [editedItems, setEditedItems] = useState([]);
  const [newEditProductId, setNewEditProductId] = useState('');
  const [verifyItems, setVerifyItems] = useState([]);
  const [inlineEdit, setInlineEdit] = useState(null); // { index, field } of the verify cell being edited
  const [showVerifyMissing, setShowVerifyMissing] = useState(false);
  const [bulkCsvName, setBulkCsvName] = useState('');
  const [bulkCsvError, setBulkCsvError] = useState('');
  const [loadingBulkCsv, setLoadingBulkCsv] = useState(false);
  const bulkCsvInput = useRef(null);
  const bulkCsvRequest = useRef(0);
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [scanBarcode, setScanBarcode] = useState('');
  const [accountingCostHeads, setAccountingCostHeads] = useState([]);
  const [accountingRules, setAccountingRules] = useState([]);
  const [purchaseItemCosts, setPurchaseItemCosts] = useState([]);
  const [loadingPurchaseItemCosts, setLoadingPurchaseItemCosts] = useState(false);
  const [transportChargeDrafts, setTransportChargeDrafts] = useState({});
  const [savingTransportChargeId, setSavingTransportChargeId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateField, setDateField] = useState('created'); // created (purchase date) | expected | received
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [itemVerifyModal, setItemVerifyModal] = useState({
    open: false,
    index: null,
    item: null,
  });

  const rowRefs = useRef({});

  const normalizeBarcode = (value) =>
    String(value || '').trim().replace(/\s+/g, '');

  const [chargeError, setChargeError] = useState('');
  const [removingChargeId, setRemovingChargeId] = useState(null);

  const refreshPurchaseItemCosts = useCallback(async () => {
    if (!token) return;

    setLoadingPurchaseItemCosts(true);
    setChargeError('');
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/purchase-item-costs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = await response.json();
      if (!response.ok) throw new Error(rows.message || 'Unable to load extra charges.');
      if (!Array.isArray(rows)) throw new Error('Unexpected extra charges response.');
      setPurchaseItemCosts(rows);
    } catch (error) {
      setChargeError(error.message || 'Unable to load extra charges.');
    } finally {
      setLoadingPurchaseItemCosts(false);
    }
  }, [token]);

  useEffect(() => { refreshPurchaseItemCosts(); }, [refreshPurchaseItemCosts]);

  const [loadingChargeTypes, setLoadingChargeTypes] = useState(false);
  const [chargeTypesError, setChargeTypesError] = useState('');
  const loadAccountingConfig = useCallback(async () => {
    if (!token) return;
    setLoadingChargeTypes(true);
    setChargeTypesError('');
    const headers = { Authorization: `Bearer ${token}` };
    const errors = [];
    // Each GET initializes its own tables; no separate ensure request is needed.
    // Load independently so a rules failure cannot discard the charge types.
    for (const [path, label, setRows] of [
      ['cost-heads', 'charge types', setAccountingCostHeads],
      ['allocation-rules', 'allocation rules', setAccountingRules],
    ]) {
      try {
        const response = await fetch(`${API_BASE_URL}/accounts/${path}`, { headers });
        const rows = await response.json();
        if (!response.ok) throw new Error(rows.message || `Unable to load ${label} (${response.status}).`);
        if (!Array.isArray(rows)) throw new Error(`Unexpected ${label} response.`);
        setRows(rows);
      } catch (error) {
        errors.push(error.message || `Unable to load ${label}.`);
      }
    }
    setChargeTypesError(errors.join(' '));
    setLoadingChargeTypes(false);
  }, [token]);

  useEffect(() => { loadAccountingConfig(); }, [loadAccountingConfig]);

  const removeExtraCharge = async (cost) => {
    const order = purchaseOrders.find((po) => String(po.id) === String(cost.purchase_order_id));
    if (!order || String(order.status).toLowerCase() !== 'received') return;
    if (removingChargeId) return;
    setRemovingChargeId(cost.id);
    setChargeError('');
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/purchase-item-costs/${cost.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Unable to remove extra charge.');
      }
      setPurchaseItemCosts((rows) => rows.filter((row) => String(row.id) !== String(cost.id)));
      dispatch(fetchPurchaseOrders());
    } catch (error) {
      setChargeError(error.message);
    } finally {
      setRemovingChargeId(null);
    }
  };

  const statusCounts = STATUSES.reduce((acc, status) => {
    acc[status] = purchaseOrders.filter(
      (po) => String(po.status || '').toLowerCase() === status
    ).length;
    return acc;
  }, {});

  const visiblePurchaseOrders = purchaseOrders.filter((po) => {
    const currentStatus = String(po.status || '').toLowerCase();
    const matchesStatus =
      statusFilter === 'all' || currentStatus === statusFilter;
    const searchText = [
      po.po_number,
      po.id,
      po.supplier_name,
      po.supplier_code,
      po.supplier_phone,
      po.warehouse_name,
      po.warehouse_code,
      po.remarks,
      po.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const poDate = dateFrom || dateTo ? getPoDateFor(po, dateField) : '';
    const matchesDate =
      (!dateFrom && !dateTo) ||
      (Boolean(poDate) && (!dateFrom || poDate >= dateFrom) && (!dateTo || poDate <= dateTo));

    return (
      matchesStatus &&
      matchesDate &&
      searchText.includes(String(searchTerm || '').trim().toLowerCase())
    );
  });

  const setDateRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const canEditStatus = (status) =>
    ['draft', 'sent', 'intransit'].includes(
      String(status || '').toLowerCase()
    );

  const canEditItems = (status) =>
    ['draft', 'sent', 'received', 'verified'].includes(
      String(status || '').trim().toLowerCase()
    );

  const getCostHead = (costHeadId) =>
    accountingCostHeads.find((head) => String(head.id) === String(costHeadId));

  const getCostRule = (costHeadId) =>
    accountingRules.find(
      (rule) =>
        String(rule.cost_head_id) === String(costHeadId) &&
        Boolean(rule.is_active ?? true)
    );

  const getCostAllocationMethod = (costHeadId) =>
    getCostRule(costHeadId)?.allocation_method || 'BY_VALUE';

  const getExtraCostHeads = () =>
    accountingCostHeads.filter(
      (head) =>
        Boolean(head.is_active ?? true) &&
        String(head.cost_head_code || '').toUpperCase() !== 'PURCHASE_PRICE'
    );

  const getTransportDraft = (po, items = []) => {
    return getPurchaseChargeDraft(
      transportChargeDrafts[po.id], items, getExtraCostHeads()
    );
  };

  const updateTransportChargeDraft = (poId, field, value) => {
    setTransportChargeDrafts((prev) => ({
      ...prev,
      [poId]: {
        purchase_order_item_id: '',
        cost_head_id: getExtraCostHeads()[0]?.id || '',
        base_amount: '',
        gst_amount: '',
        notes: '',
        ...(prev[poId] || {}),
        [field]: value,
      },
    }));
  };

  const saveTransportCharge = async (po, items = []) => {
    if (String(po.status).toLowerCase() !== 'received') return;
    const draft = getTransportDraft(po, items);
    const costHead = getCostHead(draft.cost_head_id);

    if (!costHead) {
      alert('Select a charge type.');
      return;
    }

    const targetItems =
      draft.purchase_order_item_id === '__all__'
        ? items
        : items.filter(
            (row) => String(row.id) === String(draft.purchase_order_item_id)
          );

    if (!targetItems.length) {
      alert('No purchase item found for this charge.');
      return;
    }

    const baseAmount = Number(draft.base_amount || 0);
    const gstAmount = Number(draft.gst_amount || 0);

    if (baseAmount <= 0 && gstAmount <= 0) {
      alert('Enter charge amount.');
      return;
    }

    setSavingTransportChargeId(po.id);

    try {
      await Promise.all(
        targetItems.map(async (item) => {
          const productBarcodeId =
            item.product_barcode_id ||
            item.product_barcode_id_fk ||
            item.barcode_id ||
            getProductBarcodeId(item);
          const allocatedBase =
            draft.purchase_order_item_id === '__all__'
              ? allocateByLineValue(baseAmount, items, item)
              : baseAmount;
          const allocatedGst =
            draft.purchase_order_item_id === '__all__'
              ? allocateByLineValue(gstAmount, items, item)
              : gstAmount;

          const response = await fetch(`${API_BASE_URL}/accounts/purchase-item-costs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              purchase_order_id: po.id,
              purchase_order_item_id: item.id,
              product_barcode_id: productBarcodeId,
              inventory_product_id: null,
              batch_id: null,
              cost_head_id: costHead.id,
              ledger_id: costHead.ledger_id || null,
              allocation_method:
                draft.purchase_order_item_id === '__all__'
                  ? 'BY_VALUE'
                  : getCostAllocationMethod(costHead.id),
              base_amount: allocatedBase,
              allocated_amount: allocatedBase,
              gst_amount: allocatedGst,
              total_amount: allocatedBase + allocatedGst,
              notes: draft.notes || `${costHead.cost_head_name || 'Extra'} charges`,
            }),
          });

          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.message || 'Transport charge save failed');
          }
        })
      );

      setTransportChargeDrafts((prev) => ({
        ...prev,
        [po.id]: {
          purchase_order_item_id: draft.purchase_order_item_id || targetItems[0]?.id || '',
          cost_head_id: costHead.id || '',
          base_amount: '',
          gst_amount: '',
          notes: '',
        },
      }));

      await refreshPurchaseItemCosts();
      dispatch(fetchInventoryProducts());
      dispatch(fetchStockTransactions());
      alert('Charge added.');
    } catch (error) {
      alert(error.message || 'Charge save failed');
    } finally {
      setSavingTransportChargeId(null);
    }
  };

  const getItemLineValue = (item) =>
    Math.max(0, Number(getEffectivePrice(item) || 0)) *
    Math.max(0, Number(item?.no_of_units || 0));

  const allocateByLineValue = (amount, items, targetItem) => {
    const totalAmount = Math.max(0, Number(amount || 0));
    if (!totalAmount || !items.length) return 0;

    const weights = items.map(getItemLineValue);
    const totalWeight = weights.reduce((sum, value) => sum + value, 0);
    const targetIndex = items.findIndex(
      (item) => String(item.id) === String(targetItem?.id)
    );
    if (targetIndex < 0) return 0;

    if (totalWeight <= 0) {
      const equalShare = Number((totalAmount / items.length).toFixed(2));
      return targetIndex === items.length - 1
        ? Number((totalAmount - equalShare * (items.length - 1)).toFixed(2))
        : equalShare;
    }

    const allocations = weights.map((weight, index) =>
      index === weights.length - 1
        ? 0
        : Number(((totalAmount * weight) / totalWeight).toFixed(2))
    );
    allocations[allocations.length - 1] = Number(
      (totalAmount - allocations.reduce((sum, value) => sum + value, 0)).toFixed(2)
    );
    return allocations[targetIndex];
  };

  const getSavedItemExtraCost = (po, item) =>
    getPoPurchaseCosts(po)
      .filter(
        (row) => String(row.purchase_order_item_id) === String(item?.id)
      )
      .reduce((sum, row) => sum + Number(row.total_amount || 0), 0);

  const getItemBarcodes = (item) =>
    [
      item?.mk_barcode,
      item?.barcode,
      item?.product_barcode,
      item?.vendor_barcode,
      item?.supplier_barcode,
    ]
      .map((code) => normalizeBarcode(code))
      .filter(Boolean);

  const getProductBarcodeId = (item) => {
    if (item?.product_barcode_id) return item.product_barcode_id;
    if (item?.product_barcode_id_fk) return item.product_barcode_id_fk;
    if (item?.barcode_id) return item.barcode_id;

    const itemMk = normalizeBarcode(item?.mk_barcode);
    const itemBarcode = normalizeBarcode(item?.barcode);

    const matchedByBarcode = productBarcodes.find((p) => {
      const pMk = normalizeBarcode(p.mk_barcode);
      const pBarcode = normalizeBarcode(p.barcode);

      return (
        (itemMk && pMk && itemMk === pMk) ||
        (itemBarcode && pBarcode && itemBarcode === pBarcode)
      );
    });

    if (matchedByBarcode?.id) return matchedByBarcode.id;

    const matchedByIds = productBarcodes.find(
      (p) =>
        String(p.product_id) === String(item?.product_id) &&
        String(p.brand_id) === String(item?.brand_id) &&
        String(p.category_id) === String(item?.category_id) &&
        String(p.unit_id) === String(item?.unit_id)
    );

    return matchedByIds?.id || '';
  };

  const getDisplayItems = (po) => {
    if (Array.isArray(po.items) && po.items.length > 0) {
      return po.items.map((item) => ({
        ...item,
        product_barcode_id:
          item.product_barcode_id ||
          item.product_barcode_id_fk ||
          item.barcode_id ||
          getProductBarcodeId(item),
      }));
    }

    if (Array.isArray(po.bill_details?.items)) {
      return po.bill_details.items.map((item, index) => {
        const row = {
          id: item.id || `bill-${index}`,
          product_barcode_id:
            item.product_barcode_id ||
            item.product_barcode_id_fk ||
            item.barcode_id ||
            '',
          product_id: item.product_id,
          product_name: item.product_name || `Product ${item.product_id}`,
          product_code: item.product_code || '-',
          mk_barcode: item.mk_barcode || '',
          barcode: item.barcode || '',
          category_id: item.category_id,
          category_name: item.category_name || '-',
          brand_id: item.brand_id,
          brand_name: item.brand_name || '-',
          unit_id: item.unit_id,
          unit_name: item.unit_name || '-',
          unit_short_code: item.unit_short_code || '',
          qty: item.qty,
          no_of_units: item.no_of_units || 1,
          expected_unit_price:
            item.expected_unit_price || item.unit_price || item.price || 0,
          actual_unit_price: item.actual_unit_price ?? null,
          is_verified: Boolean(item.is_verified),
          exp_date: item.exp_date || '',
          mfg_date: item.mfg_date || '',
          sku_id: item.sku_id || '',
          inventory_remarks: item.inventory_remarks || '',
        };

        row.product_barcode_id = row.product_barcode_id || getProductBarcodeId(row);

        return row;
      });
    }

    return [];
  };

  const getEffectivePrice = (item) =>
    item.actual_unit_price !== null && item.actual_unit_price !== undefined
      ? Number(item.actual_unit_price)
      : Number(item.expected_unit_price || 0);

  const getPoPurchaseCosts = (po) =>
    purchaseItemCosts.filter(
      (row) => String(row.purchase_order_id) === String(po.id)
    );

  const getPoExtraCostTotal = (po) =>
    getPoPurchaseCosts(po).reduce(
      (sum, row) => sum + Number(row.total_amount || 0),
      0
    );

  const addVerifyProduct = () => {
    const first = productBarcodes[0];

    if (!first) {
      alert('No catalog products available to add.');
      return;
    }

    setVerifyItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        is_new: true,
        product_barcode_id: first.id,
        product_id: Number(first.product_id),
        product_name:
          first.product_name_eng ||
          first.product_name_tel ||
          first.product_code ||
          '',
        product_code: first.product_code || '',
        mk_barcode: first.mk_barcode || '',
        barcode: first.barcode || '',
        category_id: Number(first.category_id),
        category_name:
          first.category_name_english || first.category_name || '-',
        brand_id: Number(first.brand_id),
        brand_name: getBrandName(first) || '-',
        unit_id: Number(first.unit_id),
        unit_name: first.unit_name || first.unit_short_code || '-',
        unit_short_code: first.unit_short_code || '',
        qty: Number(first.quantity || 1),
        no_of_units: 1,
        expected_unit_price: 0,
        actual_unit_price: 0,
        exp_date: '',
        mfg_date: '',
        sku_id: '',
        inventory_remarks: 'Added during verification',
        is_verified: false,
      },
    ]);
  };

  const removeVerifyProduct = (index) => {
    const ok = window.confirm('Remove this product from verification?');
    if (!ok) return;

    setVerifyItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateVerifyProduct = (index, barcodeRowId) => {
    const selected = productBarcodes.find(
      (p) => String(p.id) === String(barcodeRowId)
    );

    if (!selected) return;

    setVerifyItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              product_barcode_id: selected.id,
              is_verified: false,
              catalogSelectionMissing: false,
              product_id: Number(selected.product_id),
              product_name:
                selected.product_name_eng ||
                selected.product_name_tel ||
                selected.product_code ||
                '',
              product_code: selected.product_code || '',
              mk_barcode: selected.mk_barcode || '',
              barcode: selected.barcode || '',
              category_id: Number(selected.category_id),
              category_name:
                selected.category_name_english || selected.category_name || '-',
              brand_id: Number(selected.brand_id),
              brand_name: getBrandName(selected) || '-',
              unit_id: Number(selected.unit_id),
              unit_name: selected.unit_name || selected.unit_short_code || '-',
              unit_short_code: selected.unit_short_code || '',
              qty: Number(selected.quantity || 1),
            }
          : item
      )
    );
  };

  const renderVerifyCatalogSelect = (item, index, field, label) => {
    const candidates = productBarcodes.filter(p => String(p.product_id) === String(item.product_id));
    const name = p => field === 'brand_id' ? getBrandName(p) : field === 'category_id' ? (p.category_name_english || p.category_name) : (p.unit_name || p.unit_short_code);
    const catalog = field === 'brand_id' ? brands : field === 'category_id' ? categories : units;
    const options = [...new Map([
      ...candidates.filter(p => p[field] != null).map(p => [String(p[field]), { value: p[field], label: name(p) || p[field] }]),
      ...catalog.map(p => [String(p.id), { value: p.id, label: p.en_name || p.brand_name_english || p.category_name_english || p.unit_name || p.name || p.id }]),
    ]).values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return <select aria-label={`${label} for added product ${index + 1}`} value={item[field] ?? ''} disabled={Boolean(savingVerifyId)} onClick={e => e.stopPropagation()} onChange={e => {
      const next = { ...item, [field]: Number(e.target.value) };
      const matching = candidates.filter(p => ['category_id', 'brand_id', 'unit_id'].every(key => String(p[key]) === String(next[key])));
      const matched = matching.find(p => Number(p.quantity) === Number(item.qty)) || matching[0];
      if (matched) updateVerifyProduct(index, matched.id);
      else setVerifyItems(previous => previous.map((row, i) => i === index ? {
        ...next, [field.replace('_id', '_name')]: options.find(option => String(option.value) === e.target.value)?.label || '',
        product_barcode_id: null, mk_barcode: '', barcode: '', catalogSelectionMissing: true, is_verified: false,
      } : row));
    }} className="border rounded px-2 py-1 w-full">
      {!options.some(option => String(option.value) === String(item[field])) && <option value={item[field] ?? ''}>{item[field.replace('_id', '_name')] || 'Select'}</option>}
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>;
  };

  const buildPurchaseOrderText = (po) => {
    const items = getDisplayItems(po);

    let text = `*MANAKIRANA PURCHASE ORDER*\n\n`;
    text += `PO No: ${po.po_number || po.id}\n`;
    text += `Status: ${po.status || '-'}\n`;
    text += `Supplier: ${po.supplier_name || po.supplier_id || '-'}\n`;
    text += `Supplier Phone: ${po.supplier_phone || '-'}\n`;
    text += `Warehouse: ${po.warehouse_name || po.warehouse_id || '-'}\n`;
    text += `Expected Date: ${
      po.expected_date ? String(po.expected_date).slice(0, 10) : '-'
    }\n`;
    text += `Remarks: ${po.remarks || '-'}\n\n`;
    text += `*Items*\n`;

    items.forEach((item, index) => {
      text += `${index + 1}. ${item.product_name || item.product_id}\n`;
      text += `   Code: ${item.product_code || '-'}\n`;
      text += `   MK Barcode: ${normalizeBarcode(item.mk_barcode) || '-'}\n`;
      text += `   Brand: ${item.brand_name || '-'}\n`;
      text += `   Category: ${item.category_name || '-'}\n`;
      text += `   Qty: ${Number(item.qty || 0).toFixed(3)} ${
        item.unit_short_code || item.unit_name || ''
      }\n`;
      text += `   No. Units: ${Number(item.no_of_units || 1).toFixed(0)}\n\n`;
    });

    text += `Please supply the above items.`;

    return text;
  };

  const sharePurchaseOrderWhatsApp = (po) => {
    const message = encodeURIComponent(buildPurchaseOrderText(po));
    const phone = String(po.supplier_phone || '').replace(/\D/g, '');
    const url = phone
      ? `https://wa.me/91${phone.slice(-10)}?text=${message}`
      : `https://wa.me/?text=${message}`;

    const shareWindow = window.open(url, '_blank');
    if (!shareWindow) alert('Please allow popups for this site to open WhatsApp.');
    else shareWindow.opener = null;
  };

  const printPurchaseOrder = (po) => {
    const items = getDisplayItems(po);
    const printWindow = window.open('', '_blank', 'width=620,height=880');

    if (!printWindow) {
      alert('Please allow popup to print purchase order.');
      return;
    }

    const rows = items
      .map(
        (item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>
              <strong>${item.product_name || item.product_id || '-'}</strong><br/>
              <small>Code: ${item.product_code || '-'}</small><br/>
              <small>MK: ${normalizeBarcode(item.mk_barcode) || '-'}</small>
            </td>
            <td>${item.category_name || '-'}</td>
            <td>${item.brand_name || '-'}</td>
            <td>${item.unit_name || item.unit_short_code || '-'}</td>
            <td class="right">${Number(item.qty || 0).toFixed(3)}</td>
            <td class="right">${Number(item.no_of_units || 1).toFixed(0)}</td>
          </tr>
        `
      )
      .join('');

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Purchase Order ${po.po_number || po.id}</title>
          <style>
            @page { size: 148mm 210mm; margin: 8mm; }
            @page purchasePortrait { size: 148mm 210mm; margin: 8mm; }
            * { box-sizing: border-box; }
            body { page: purchasePortrait; font-family: Arial, sans-serif; width: 132mm; max-width: 100%; margin: 0 auto; padding: 0; color: #111827; }
            h1 { text-align: center; margin: 0; font-size: 16px; }
            .sub { text-align: center; margin-top: 3px; font-size: 10px; color: #555; }
            .info {
              display: grid;
              grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
              gap: 5px 12px;
              margin: 14px 0;
              font-size: 10px;
            }
            .info div { border-bottom: 1px solid #eee; padding-bottom: 3px; overflow-wrap: anywhere; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; }
            th, td { border: 1px solid #d1d5db; padding: 4px; vertical-align: top; overflow-wrap: anywhere; }
            th:nth-child(1) { width: 6%; }
            th:nth-child(2) { width: 37%; }
            th:nth-child(3) { width: 13%; }
            th:nth-child(4) { width: 13%; }
            th:nth-child(5) { width: 10%; }
            th:nth-child(6) { width: 12%; }
            th:nth-child(7) { width: 9%; }
            small { font-size: 8px; }
            thead { display: table-header-group; }
            tr { break-inside: avoid; }
            th { background: #eff6ff; text-align: left; }
            .right { text-align: right; }
            .footer {
              margin-top: 24px;
              display: flex;
              justify-content: space-between;
              font-size: 10px;
              break-inside: avoid;
            }
            @media print {
              button { display: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h1>MANAKIRANA PURCHASE ORDER</h1>
          <div class="sub">Supplier Copy - No Internal Price / Amount</div>

          <div class="info">
            <div><strong>PO No:</strong> ${po.po_number || po.id || '-'}</div>
            <div><strong>Status:</strong> ${po.status || '-'}</div>
            <div><strong>Supplier:</strong> ${
              po.supplier_name || po.supplier_id || '-'
            }</div>
            <div><strong>Supplier Phone:</strong> ${po.supplier_phone || '-'}</div>
            <div><strong>Warehouse:</strong> ${
              po.warehouse_name || po.warehouse_id || '-'
            }</div>
            <div><strong>Warehouse Code:</strong> ${po.warehouse_code || '-'}</div>
            <div><strong>Expected Date:</strong> ${
              po.expected_date ? String(po.expected_date).slice(0, 10) : '-'
            }</div>
            <div><strong>Remarks:</strong> ${po.remarks || '-'}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Product</th>
                <th>Category</th>
                <th>Brand</th>
                <th>Unit</th>
                <th>Qty</th>
                <th>No. Units</th>
              </tr>
            </thead>
            <tbody>
              ${
                rows ||
                `<tr>
                  <td colspan="7" style="text-align:center;">No items</td>
                </tr>`
              }
            </tbody>
          </table>

          <div class="footer">
            <div>Prepared By</div>
            <div>Supplier Signature</div>
          </div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const startEditStatus = (po) => {
    setEditingId(po.id);
    setForm({
      status: po.status || 'draft',
      expected_date: po.expected_date
        ? String(po.expected_date).slice(0, 10)
        : '',
      arrived_date: po.arrived_date ? String(po.arrived_date).slice(0, 10) : '',
      remarks: po.remarks || '',
    });
  };

  const cancelEditStatus = () => {
    setEditingId(null);
    setForm({});
  };

  const saveStatus = async (po) => {
    if (String(form.status || '').toLowerCase() === 'verified') {
      alert('Use Verify to update stock. Status edit cannot mark an order as verified.');
      return;
    }

    await dispatch(
      updatePurchaseOrder({
        id: po.id,
        payload: {
          status: form.status,
          expected_date: form.expected_date || null,
          arrived_date: form.arrived_date || null,
          remarks: form.remarks || null,
        },
      })
    );

    dispatch(fetchPurchaseOrders());
    cancelEditStatus();
  };

  const startEditItems = (po) => {
    if (!canEditItems(po.status)) return;
    setVerifyingId(null);
    setNewEditProductId('');
    setEditingItemsId(po.id);
    setOpenOrderId(po.id);

    setEditedItems(
      getDisplayItems(po).map((item) => ({
        ...item,
        product_barcode_id:
          item.product_barcode_id ||
          item.product_barcode_id_fk ||
          item.barcode_id ||
          getProductBarcodeId(item),
        product_id: Number(item.product_id),
        category_id: Number(item.category_id),
        brand_id: Number(item.brand_id),
        unit_id: Number(item.unit_id),
        qty: Number(item.qty || 0),
        no_of_units: Number(item.no_of_units || 1),
        expected_unit_price: Number(item.expected_unit_price || 0),
        actual_unit_price: item.actual_unit_price ?? null,
        mk_barcode: item.mk_barcode || '',
        barcode: item.barcode || '',
      }))
    );
  };

  const cancelEditItems = () => {
    setNewEditProductId('');
    setEditingItemsId(null);
    setEditedItems([]);
  };

  const updateEditedItem = (index, field, value) => {
    const copy = [...editedItems];
    copy[index] = { ...copy[index], [field]: value };
    setEditedItems(copy);
  };

  const updateEditedProduct = (index, barcodeRowId) => {
    const selected = productBarcodes.find(
      (p) => String(p.id) === String(barcodeRowId)
    );

    if (!selected) return;

    const copy = [...editedItems];

    copy[index] = {
      no_of_units: 1,
      expected_unit_price: 0,
      actual_unit_price: null,
      ...copy[index],
      product_barcode_id: selected.id,
      product_id: Number(selected.product_id),
      product_name:
        selected.product_name_eng ||
        selected.product_name_tel ||
        selected.product_code ||
        '',
      product_code: selected.product_code || '',
      mk_barcode: selected.mk_barcode || '',
      barcode: selected.barcode || '',
      category_id: Number(selected.category_id),
      category_name:
        selected.category_name_english || selected.category_name || '-',
      brand_id: Number(selected.brand_id),
      brand_name: getBrandName(selected) || '-',
      unit_id: Number(selected.unit_id),
      unit_name: selected.unit_name || selected.unit_short_code || '-',
      unit_short_code: selected.unit_short_code || '',
      qty: Number(selected.quantity || 1),
    };

    setEditedItems(copy);
  };

  const removeEditedItem = (index) => {
    setEditedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveItems = async (po) => {
    if (!canEditItems(po.status)) return;
    if (editedItems.length === 0) {
      alert('At least one product is required in purchase order.');
      return;
    }

    try {
    await dispatch(
      updatePurchaseOrderItems({
        id: po.id,
        items: editedItems.map((item) => ({
          id: item.id,
          product_barcode_id:
            item.product_barcode_id ||
            item.product_barcode_id_fk ||
            item.barcode_id ||
            getProductBarcodeId(item),
          product_id: Number(item.product_id),
          category_id: Number(item.category_id),
          brand_id: Number(item.brand_id),
          unit_id: Number(item.unit_id),
          qty: Number(item.qty),
          no_of_units: Number(item.no_of_units || 1),
          expected_unit_price: Number(item.expected_unit_price || 0),
          actual_unit_price: item.actual_unit_price ?? null,
          product_name: item.product_name,
          product_code: item.product_code,
          category_name: item.category_name,
          brand_name: item.brand_name,
          unit_name: item.unit_name,
          unit_short_code: item.unit_short_code,
          mk_barcode: item.mk_barcode,
          barcode: item.barcode,
        })),
      })
    ).unwrap();

    dispatch(fetchPurchaseOrders());
    refreshPurchaseItemCosts();
    cancelEditItems();
    } catch (error) {
      alert(typeof error === 'string' ? error : error.message || 'Unable to save purchase items.');
    }
  };

  const startVerify = (po) => {
    cancelEditItems();
    // View and Verify reopen the same draft without discarding entered details.
    if (verifyingId === po.id) {
      setOpenOrderId(po.id);
      return;
    }
    setVerifyingId(po.id);
    setInlineEdit(null);
    setShowVerifyMissing(false);
    bulkCsvRequest.current += 1;
    setBulkCsvName('');
    setBulkCsvError('');
    setLoadingBulkCsv(false);
    setOpenOrderId(po.id);
    setVerifyRemarks(po.remarks || '');
    setScanBarcode('');


    setVerifyItems(
      getDisplayItems(po).map((item) => ({
        ...item,
        product_barcode_id:
          item.product_barcode_id ||
          item.product_barcode_id_fk ||
          item.barcode_id ||
          getProductBarcodeId(item),
          actual_unit_price:
            item.actual_unit_price ?? item.expected_unit_price ?? 0,
          unit_mrp: item.unit_mrp ?? item.unit_MRP ?? item.mrp ?? item.MRP ?? '',
          is_verified: Boolean(item.is_verified),
        mk_barcode: item.mk_barcode || '',
        barcode: item.barcode || '',
        exp_date: item.exp_date || '',
        mfg_date: item.mfg_date || '',
        sku_id: item.sku_id || '',
        inventory_remarks: item.inventory_remarks || '',
      }))
    );
  };

  const cancelVerify = () => {
    if (savingVerifyId) return;
    bulkCsvRequest.current += 1;
    setBulkCsvName('');
    setBulkCsvError('');
    setLoadingBulkCsv(false);
    setOpenOrderId(null);
    setVerifyingId(null);
    setVerifyItems([]);
    setInlineEdit(null);
    setShowVerifyMissing(false);

    setVerifyRemarks('');
    setSavingVerifyProgress('');
    setScanBarcode('');
    setItemVerifyModal({
      open: false,
      index: null,
      item: null,
    });
  };

  const saveItemInventoryDetails = (updatedItem) => {
    setVerifyItems((prev) =>
      prev.map((item, i) =>
        i === itemVerifyModal.index
          ? {
              ...item,
              ...updatedItem,
              product_barcode_id:
                updatedItem.product_barcode_id ||
                item.product_barcode_id ||
                getProductBarcodeId(item),
              is_verified: true,
            }
          : item
      )
    );

    setItemVerifyModal({
      open: false,
      index: null,
      item: null,
    });
  };

  // Confirm / un-confirm a row directly. Entered values are kept either way.
  const toggleVerifyItem = (index) => {
    setVerifyItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, is_verified: !row.is_verified } : row))
    );
  };

  const setAllVerified = (value) => {
    setVerifyItems((prev) => prev.map((row) => ({ ...row, is_verified: value })));
  };

  const commitInlineEdit = (index, field, raw) => {
    setVerifyItems((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        let value = typeof raw === 'string' ? raw.trim() : raw;
        if (VERIFY_NUMERIC_FIELDS.has(field)) {
          if (value === '') {
            if (field === 'qty' || field === 'no_of_units') return row;
          } else {
            const n = Number(value);
            if (!Number.isFinite(n) || n < 0) return row;
            if (field === 'no_of_units' && n < 1) return row;
            value = n;
          }
        }
        if (String(row[field] ?? '') === String(value ?? '')) return row;
        const next = { ...row, [field]: value };
        if (field === 'unit_mrp') {
          delete next.unit_MRP;
          delete next.mrp;
          delete next.MRP;
        }
        // A price review done in Item Details is tied to the old values; it must be redone.
        if (row.pricing_publication && VERIFY_RECEIPT_FIELDS.has(field)) {
          delete next.pricing_publication;
          next.is_verified = false;
        } else if (!row.is_verified && !row.pricing_publication && getMissingVerifyFields(next).length === 0 && getMissingVerifyFields(row).length > 0) {
          // Auto-confirm once the required details are filled in.
          next.is_verified = true;
        }
        return next;
      })
    );
  };

  const moveInlineEdit = (index, field, move) => {
    if (!move) return setInlineEdit(null);
    const fi = VERIFY_EDIT_FIELDS.indexOf(field);
    let nextIndex = index;
    let nextField = field;
    if (move === 'down') nextIndex = index + 1;
    else if (move === 'up') nextIndex = index - 1;
    else if (move === 'next') {
      if (fi + 1 < VERIFY_EDIT_FIELDS.length) nextField = VERIFY_EDIT_FIELDS[fi + 1];
      else { nextField = VERIFY_EDIT_FIELDS[0]; nextIndex = index + 1; }
    } else if (move === 'prev') {
      if (fi > 0) nextField = VERIFY_EDIT_FIELDS[fi - 1];
      else { nextField = VERIFY_EDIT_FIELDS[VERIFY_EDIT_FIELDS.length - 1]; nextIndex = index - 1; }
    }
    if (nextIndex < 0 || nextIndex >= verifyItems.length) return setInlineEdit(null);
    setInlineEdit({ index: nextIndex, field: nextField });
  };

  const confirmItemByBarcode = (barcode) => {
    const scanned = normalizeBarcode(barcode);
    if (!scanned) return;

    const index = verifyItems.findIndex((item) =>
      getItemBarcodes(item).includes(scanned)
    );

    if (index === -1) {
      alert(`Barcode not found: ${scanned}`);
      setScanBarcode('');
      return;
    }

    setVerifyItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, is_verified: true } : row))
    );
    const firstMissing = getMissingVerifyFields(verifyItems[index])[0];
    if (firstMissing) setInlineEdit({ index, field: firstMissing });

    setTimeout(() => {
      rowRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      rowRefs.current[index]?.focus?.();
    }, 100);

    setScanBarcode('');
  };

  const uploadBulkCsv = async (event, po) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const request = ++bulkCsvRequest.current;
    setBulkCsvError('');
    setBulkCsvName('');
    setLoadingBulkCsv(true);
    try {
      if (!/\.csv$/i.test(file.name)) throw new Error('Please choose a CSV file.');
      const text = await file.text();
      if (request !== bulkCsvRequest.current) return;
      const imported = importPurchaseVerificationCsv(text, po, verifyItems);
      setVerifyItems(imported);
      setBulkCsvName(file.name);
    } catch (error) {
      if (request === bulkCsvRequest.current) setBulkCsvError(error.message || 'Unable to read CSV.');
    } finally {
      if (request === bulkCsvRequest.current) setLoadingBulkCsv(false);
    }
  };

  const saveVerify = async (po) => {
    if (savingVerifyId) return;
    setSavingVerifyId(po.id);
    setSavingVerifyProgress('Checking items...');
    setInlineEdit(null);

    try {
      // Fill auto SKU for confirmed rows that were left blank.
      const preparedItems = verifyItems.map((item) =>
        item.is_verified && !item.sku_id ? { ...item, sku_id: makeSkuId(item) } : item
      );
      setVerifyItems(preparedItems);
      const notVerified = preparedItems.filter((item) => !item.is_verified);

      if (notVerified.length > 0) {
        const approveWithRemarks = window.confirm(
          `Pending items: ${notVerified.length}\n\nDo you want to approve with remarks?`
        );

        if (!approveWithRemarks) return;

        if (!verifyRemarks.trim()) {
          alert('Please enter remarks before approving with missing items.');
          return;
        }
      }

      const verifiedItems = preparedItems.filter((item) => item.is_verified);
      if (preparedItems.some(item => item.is_new && item.catalogSelectionMissing)) {
        alert('A selected product, brand, category and unit combination has no catalog barcode. Add that combination in Catalog → Product Barcodes, reload, then verify.');
        return;
      }

      if (verifiedItems.length === 0) {
        alert('Please verify at least one item.');
        return;
      }

      const verifiedWithMissingDetails = verifiedItems.filter(
        (item) => getMissingVerifyFields(item).length > 0 || !item.sku_id
      );

      if (verifiedWithMissingDetails.length > 0) {
        setShowVerifyMissing(true);
        const names = verifiedWithMissingDetails
          .slice(0, 8)
          .map((item) => `• ${getProductDisplayName(item)}`)
          .join('\n');
        alert(
          `Enter Actual Price, Unit MRP and Expiry Date for ${verifiedWithMissingDetails.length} confirmed item(s). Missing cells are marked in red.\n\n${names}`
        );
        return;
      }

      const batchId = makeBatchId();
      const inventoryByItemId = {};

      const existingVerifiedItems = preparedItems.filter((item) => !item.is_new);

      setSavingVerifyProgress('Updating purchase order...');

      const verifyResult = await dispatch(
        verifyReceivedPurchaseOrder({
          id: po.id,
          remarks:
            notVerified.length > 0
              ? `APPROVED WITH MISSING ITEMS: ${verifyRemarks}`
              : verifyRemarks || null,
          items: existingVerifiedItems.map((item) => ({
            id: item.id,
            actual_unit_price: Number(item.actual_unit_price || 0),
            is_verified: Boolean(item.is_verified),
            missing: !item.is_verified,
            inventory_remarks: item.inventory_remarks || null,
          })),
        })
      );

      if (!verifyReceivedPurchaseOrder.fulfilled.match(verifyResult)) {
        alert(verifyResult.payload || 'Purchase verification failed');
        return;
      }

      const receiveInventoryItem = async (item) => {
        const productBarcodeId =
          item.product_barcode_id ||
          item.product_barcode_id_fk ||
          item.barcode_id ||
          getProductBarcodeId(item);

        if (!productBarcodeId) {
          throw new Error(
            `Product barcode id missing for ${
              item.product_name || item.product_id
            }. Please select the product again or check catalog barcode data.`
          );
        }

        const payload = {
          purchase_order_id: po.id,
          purchase_order_item_id: item.is_new ? null : item.id,
          product_barcode_id: productBarcodeId,
          product_id: item.product_id,
          batch_id: batchId,
          sku_id: item.sku_id,
          warehouse_id: po.warehouse_id,
          supplier_id: po.supplier_id || po.stakeholders_id,
          stakeholders_id: po.stakeholders_id || po.supplier_id,
          qty: Number(item.qty || 0),
          no_of_units: Number(item.no_of_units || 1),
          unit_price: Number(item.actual_unit_price || 0),
          unit_mrp: Number(item.unit_mrp || item.unit_MRP || item.mrp || item.MRP || 0),
          mfg_date: item.mfg_date || null,
          exp_date: item.exp_date,
          remarks: item.inventory_remarks || verifyRemarks || null,
        };

        if(item.pricing_publication){
          const reviewed=item.pricing_publication.receipt;
          for(const field of ['unit_price','unit_mrp','qty','no_of_units','warehouse_id','product_barcode_id','exp_date']){
            if(String(reviewed[field])!==String(payload[field]))throw new Error('Purchase details changed after pricing review. Verify the product again.');
          }
          const response=await fetch(`${API_BASE_URL}/inventory-pg/pricing-workspace/purchase/publish`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},body:JSON.stringify(item.pricing_publication)});
          const result=await response.json();
          if(!response.ok)throw new Error(result.message||'Purchase pricing could not be published');
          if(result.pricingPublication?.state!=='complete')throw new Error(`Stock received; pricing sync is pending. Retry Save Verification to resume without adding stock again. ${result.message||''}`);
          return {item,productBarcodeId,inventoryProduct:result.inventoryProduct};
        }
        const inventoryResult = await dispatch(
          receiveVerifiedPurchaseToInventory(payload)
        );

        if (receiveVerifiedPurchaseToInventory.rejected.match(inventoryResult)) {
          throw new Error(
            inventoryResult.payload ||
              `Inventory update failed for ${item.product_name || item.product_id}`
          );
        }

        const inventoryProduct = inventoryResult.payload?.inventoryProduct || null;
        return { item, productBarcodeId, inventoryProduct };
      };

      const chunkSize = 3;
      for (let start = 0; start < verifiedItems.length; start += chunkSize) {
        const chunk = verifiedItems.slice(start, start + chunkSize);
        setSavingVerifyProgress(
          `Adding inventory ${Math.min(start + chunk.length, verifiedItems.length)} of ${verifiedItems.length}...`
        );

        const chunkResults = await Promise.all(
          chunk.map((item) => receiveInventoryItem(item))
        );

        chunkResults.forEach(({ item, productBarcodeId, inventoryProduct }) => {
          inventoryByItemId[String(item.id || productBarcodeId)] = inventoryProduct;
        });
      }

      setSavingVerifyProgress('Refreshing lists...');

      alert('Purchase verified, inventory updated, and stock movement added.');

      await refreshPurchaseItemCosts();
      dispatch(fetchPurchaseOrders());
      dispatch(fetchInventoryProducts());
      dispatch(fetchStockTransactions());

      setVerifyingId(null);
      setVerifyItems([]);
      setShowVerifyMissing(false);

      setVerifyRemarks('');
      setSavingVerifyProgress('');
      setScanBarcode('');
      setItemVerifyModal({
        open: false,
        index: null,
        item: null,
      });
    } catch (error) {
      alert(error.message || 'Purchase verification failed');
    } finally {
      setSavingVerifyId(null);
      setSavingVerifyProgress('');
    }
  };

  return (
    <section className="purchase-orders-section min-w-0 space-y-4">
      {documentOrder && <PurchaseOrderDocuments key={documentOrder.id} order={documentOrder} purchaseAmount={(() => { const amounts = getPurchaseAmounts(getDisplayItems(documentOrder)); return amounts.actual ?? amounts.expected; })()} onClose={() => setDocumentOrderId(null)} />}
      {['all'].map((status) => {
        const style = {
          section: 'bg-white border-slate-200',
          header: 'bg-slate-100 text-slate-800',
          badge: 'bg-slate-200 text-slate-800',
        };
        const rows = visiblePurchaseOrders;

        return (
          <div
            key={status}
            className={`rounded-xl border shadow-sm p-4 ${style.section}`}
          >
            <div
              className={`rounded-lg px-4 py-3 mb-3 flex flex-wrap gap-3 justify-between items-center ${style.header}`}
            >
              <div>
                <h2 className="font-bold text-lg">Purchase Orders</h2>
                <div className="mt-1 text-xs text-slate-600">
                  Showing {rows.length} of {purchaseOrders.length} orders
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 max-w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                  placeholder="Search PO, supplier, warehouse"
                />

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm capitalize"
                >
                  <option value="all">All statuses ({purchaseOrders.length})</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s} ({statusCounts[s] || 0})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex w-full flex-wrap items-center justify-end gap-2" aria-label="Date filter">
                <select
                  aria-label="Date to filter by"
                  value={dateField}
                  onChange={(e) => setDateField(e.target.value)}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="created">Purchase (created) date</option>
                  <option value="expected">Expected date</option>
                  <option value="received">Received date</option>
                </select>
                <label className="flex items-center gap-1 text-sm text-slate-700">
                  From
                  <input
                    type="date"
                    aria-label="From date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                  />
                </label>
                <label className="flex items-center gap-1 text-sm text-slate-700">
                  To
                  <input
                    type="date"
                    aria-label="To date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                  />
                </label>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setDateRange(todayKey(), todayKey())} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">Today</button>
                  <button type="button" onClick={() => setDateRange(todayKey(-1), todayKey(-1))} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">Yesterday</button>
                  <button type="button" onClick={() => setDateRange(todayKey(-6), todayKey())} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">Last 7 days</button>
                  <button type="button" onClick={() => setDateRange(todayKey().slice(0, 8) + '01', todayKey())} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">This month</button>
                  {(dateFrom || dateTo) && (
                    <button type="button" onClick={() => setDateRange('', '')} className="rounded px-3 py-2 text-sm text-red-700 underline">Clear dates</button>
                  )}
                </div>
              </div>
            </div>

            <div className="purchase-table-scroll min-w-0 bg-white rounded-lg border">
              <table className="purchase-responsive-table purchase-order-summary w-full text-sm">
                <thead className={style.header}>
                  <tr>
                    <th className="p-2 text-left">PO No</th>
                    <th className="p-2 text-left">Supplier</th>
                    <th className="p-2 text-left">Warehouse</th>
                    <th className="p-2 text-right">Expected Amount</th>
<th className="p-2 text-right">Expected Amount with Charges</th>
<th className="p-2 text-right">Actual Amount</th>
<th className="p-2 text-right">Actual Amount with Charges</th>
                    <th className="p-2 text-left">Created</th>
                    <th className="p-2 text-left">Expected</th>
                    <th className="p-2 text-left">Received</th>
                    <th className="p-2 text-left">Remarks</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-center">Edit Status</th>
                    <th className="p-2 text-center">Edit Items</th>
                    <th className="p-2 text-center">Delete</th>
                    <th className="p-2 text-center">Verify</th>
                    <th className="purchase-share-print-column p-2 text-center">Share / Print</th>
                    <th className="p-2 text-center">Details</th>
                    <th className="p-2 text-center">Docs</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan="19" className="p-4 text-center text-gray-500">
                        No purchase orders match the selected filters
                      </td>
                    </tr>
                  )}

                  {rows.map((po) => {
                    const isOpen = openOrderId === po.id;
                    const isEditingStatus = editingId === po.id;
                    const isEditingItems = editingItemsId === po.id && canEditItems(po.status);
                    const isVerifying = verifyingId === po.id;
                    const isSavingVerify = savingVerifyId === po.id;

                    const displayItems = getDisplayItems(po);
                    const activeItems = isVerifying
                      ? verifyItems
                      : isEditingItems
                      ? editedItems
                      : displayItems;

                    const amounts = getPurchaseAmounts(activeItems, getPoExtraCostTotal(po));
                    const isSent =
                      String(po.status || '').toLowerCase() === 'sent';
                    const rowStatus =
                      String(po.status || '').toLowerCase();
                    const rowStyle = STATUS_STYLES[rowStatus] || style;
                    const savedPurchaseCosts = getPoPurchaseCosts(po);
                    const transportDraft = getTransportDraft(po, displayItems);
                    const isSavingTransportCharge =
                      savingTransportChargeId === po.id;

                    return (
                      <React.Fragment key={po.id}>
                        <tr className="border-t align-top hover:bg-gray-50">
                          <td data-label="PO No" className="p-2 font-medium">{po.po_number}</td>

                          <td data-label="Supplier" className="p-2">
                            <div className="font-medium">
                              {po.supplier_name || po.supplier_id}
                            </div>
                            <div className="text-xs text-gray-500">
                              {po.supplier_code || '-'} |{' '}
                              {po.supplier_phone || '-'}
                            </div>
                          </td>

                          <td data-label="Warehouse" className="p-2">
                            <div className="font-medium">
                              {po.warehouse_name || po.warehouse_id}
                            </div>
                            <div className="text-xs text-gray-500">
                              {po.warehouse_code || '-'}
                            </div>
                          </td>

                          <td data-label="Expected Amount" className="p-2 text-right font-semibold">₹{amounts.expected.toFixed(2)}</td>
                          <td data-label="Expected Amount with Charges" className="p-2 text-right font-semibold">₹{amounts.expectedWithCharges.toFixed(2)}</td>
                          <td data-label="Actual Amount" className="p-2 text-right font-semibold">{amounts.actual === null ? '—' : `₹${amounts.actual.toFixed(2)}`}</td>
                          <td data-label="Actual Amount with Charges" className="p-2 text-right font-semibold">{amounts.actualWithCharges === null ? '—' : `₹${amounts.actualWithCharges.toFixed(2)}`}</td>

                          <td data-label="Created" className="p-2">{getPoCreatedDate(po)}</td>

                          <td data-label="Expected" className="p-2">
                            {isEditingStatus ? (
                              <input
                                type="date"
                                value={form.expected_date}
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    expected_date: e.target.value,
                                  })
                                }
                                className="border rounded px-2 py-1"
                              />
                            ) : po.expected_date ? (
                              String(po.expected_date).slice(0, 10)
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Received" className="p-2">
                            {isEditingStatus ? (
                              <input
                                type="date"
                                value={form.arrived_date}
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    arrived_date: e.target.value,
                                  })
                                }
                                className="border rounded px-2 py-1"
                              />
                            ) : po.arrived_date ? (
                              String(po.arrived_date).slice(0, 10)
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Remarks" className="p-2">
                            {isEditingStatus ? (
                              <input
                                value={form.remarks}
                                onChange={(e) =>
                                  setForm({ ...form, remarks: e.target.value })
                                }
                                className="border rounded px-2 py-1"
                              />
                            ) : (
                              po.remarks || '-'
                            )}
                          </td>

                          <td data-label="Status" className="p-2">
                            {isEditingStatus ? (
                              <select
                                value={form.status}
                                onChange={(e) =>
                                  setForm({ ...form, status: e.target.value })
                                }
                                className="border rounded px-2 py-1"
                              >
                                {MANUAL_STATUS_OPTIONS.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span
                                className={`px-2 py-1 rounded-full text-xs capitalize ${rowStyle.badge}`}
                              >
                                {po.status}
                              </span>
                            )}
                          </td>

                          <td data-label="Edit Status" className="p-2 text-center">
                            {isEditingStatus ? (
                              <div className="purchase-status-actions flex gap-2 justify-center">
                                <button
                                  type="button"
                                  onClick={() => saveStatus(po)}
                                  className="bg-green-700 text-white px-3 py-1 rounded"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditStatus}
                                  className="border px-3 py-1 rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : canEditStatus(po.status) ? (
                              <button
                                type="button"
                                onClick={() => startEditStatus(po)}
                                className="text-blue-700 underline"
                              >
                                Edit
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Edit Items" className="p-2 text-center">
                            {canEditItems(po.status) ? (
                              <button
                                type="button"
                                onClick={() => startEditItems(po)}
                                className="text-blue-700 underline"
                              >
                                Edit Items
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Delete" className="p-2 text-center">
                            {po.status === 'draft' ? (
                              <button type="button" onClick={() => handleDelete(po)}
                                disabled={deletingId !== null}
                                className="text-red-700 underline disabled:opacity-50"
                                aria-label={`Delete draft ${po.po_number || po.id}`}>
                                {deletingId === po.id ? 'Deleting…' : 'Delete'}
                              </button>
                            ) : '-'}
                          </td>

                          <td data-label="Verify" className="p-2 text-center">
                            {String(po.status || '').toLowerCase() ===
                            'received' ? (
                              <button
                                type="button"
                                onClick={() => startVerify(po)}
                                className="text-emerald-700 underline"
                              >
                                Verify
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Share / Print" className="purchase-share-print-column p-2 text-center">
                            {isSent ? (
                              <div className="purchase-share-print-actions">
                                <button
                                  type="button"
                                  onClick={() => sharePurchaseOrderWhatsApp(po)}
                                  className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700"
                                >
                                  WhatsApp
                                </button>
                                <button
                                  type="button"
                                  onClick={() => printPurchaseOrder(po)}
                                  className="rounded bg-slate-700 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800"
                                >
                                  Print
                                </button>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>

                          <td data-label="Details" className="p-2 text-center">
                            <button
                              type="button"
                              onClick={() => {
                                if (isOpen) setOpenOrderId(null);
                                else if (String(po.status || '').toLowerCase() === 'received') startVerify(po);
                                else setOpenOrderId(po.id);
                              }}
                              className="text-blue-700 underline"
                            >
                              {isOpen ? 'Hide' : 'View'}
                            </button>
                          </td>
                          <td data-label="Docs" className="p-2 text-center">
                            <button type="button" onClick={() => setDocumentOrderId(po.id)} className="text-blue-700 underline"
                              aria-label={`Documents for ${po.po_number || po.id}`}>
                              {['draft', 'sent'].includes(String(po.status).toLowerCase()) ? 'View docs' : 'Upload / View'}
                            </button>
                          </td>
                        </tr>

                        {isOpen && (
                          <tr>
                            <td colSpan="19" className="purchase-order-detail-cell p-3 bg-gray-50">
                              <div className="purchase-order-detail">
                              {isVerifying && (
                                <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                  <p className="mb-3 text-sm text-emerald-800">
                                    Double-click any highlighted cell to edit it (Enter = save &amp; go down, Tab = next cell, Esc = cancel). A row is confirmed automatically once Actual Price, MRP and Expiry are filled. Use Item Details only if you want to review selling prices. Click Approve &amp; Verify once at the end.
                                  </p>
                                  <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <button type="button" disabled={isSavingVerify || loadingBulkCsv || !verifyItems.length}
                                      onClick={() => downloadPurchaseVerificationCsv(po, verifyItems)}
                                      className="rounded border border-emerald-700 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
                                      Download Purchase CSV
                                    </button>
                                    <input ref={bulkCsvInput} type="file" accept=".csv,text/csv" className="hidden"
                                      aria-label="Upload purchase verification CSV" onChange={event => uploadBulkCsv(event, po)} />
                                    <button type="button" disabled={isSavingVerify || loadingBulkCsv}
                                      onClick={() => bulkCsvInput.current?.click()}
                                      className="rounded border border-emerald-700 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
                                      {loadingBulkCsv ? 'Reading CSV...' : 'Choose File (CSV Upload)'}
                                    </button>
                                    <button type="button" disabled={!bulkCsvName || loadingBulkCsv || isSavingVerify || savingTransportChargeId === po.id}
                                      onClick={() => saveVerify(po)}
                                      className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                                      {isSavingVerify ? (savingVerifyProgress || 'Verifying...') : 'Bulk Verify'}
                                    </button>
                                  </div>
                                  <p className="mb-2 text-xs text-emerald-800">Fill actual_unit_price, unit_mrp and exp_date (YYYY-MM-DD) for every row. Keep purchase_order_id and item_id unchanged. SKU is generated when blank. Upload to review, then click Bulk Verify to save.</p>
                                  {bulkCsvName && <p role="status" className="mb-2 text-sm text-green-700">{bulkCsvName}: {verifyItems.length} items loaded for review.</p>}
                                  {bulkCsvError && <p role="alert" className="mb-2 text-sm text-red-700">{bulkCsvError}</p>}
                                  <label className="block text-xs font-semibold mb-1">
                                    Scan Barcode To Confirm Item
                                  </label>

                                  <input
                                    autoFocus
                                    value={scanBarcode}
                                    onChange={(e) =>
                                      setScanBarcode(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        confirmItemByBarcode(scanBarcode);
                                      }
                                    }}
                                    className="border rounded px-3 py-2 w-full mb-3"
                                    placeholder="Scan MK barcode or vendor barcode and press Enter"
                                  />

                                  <label className="block text-xs font-semibold mb-1">
                                    Verification Remarks
                                  </label>
                                  <input
                                    value={verifyRemarks}
                                    onChange={(e) =>
                                      setVerifyRemarks(e.target.value)
                                    }
                                    className="border rounded px-3 py-2 w-full"
                                    placeholder="Required if any item is missed"
                                  />

                                  <div className="mt-2 flex flex-wrap items-center gap-3">
                                    <div className="text-sm font-semibold">
                                      Confirmed:{' '}
                                      <span className="text-green-700">
                                        {
                                          verifyItems.filter(
                                            (i) => i.is_verified
                                          ).length
                                        }
                                      </span>{' '}
                                      / {verifyItems.length}
                                    </div>

                                    <button
                                      type="button"
                                      onClick={addVerifyProduct}
                                      className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
                                    >
                                      + Add Product
                                    </button>
                                  </div>
                                </div>
                              )}

                              {['received', 'verified'].includes(String(po.status || '').toLowerCase()) &&
                                displayItems.length > 0 && (
                                <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                      <div className="text-sm font-bold text-emerald-900">
                                        Extra Charges
                                      </div>
                                      <div className="text-xs text-emerald-700">
                                        {rowStatus === 'verified' ? 'Prices are locked. Saved extra charges are read-only.' : 'Add charges to one item or allocate by value across all items. Add Charge saves immediately, including during verification.'}
                                      </div>
                                    </div>
                                    {savedPurchaseCosts.length > 0 && (
                                      <div className="text-sm font-semibold text-emerald-900">
                                        Extra total ₹{getPoExtraCostTotal(po).toFixed(2)}
                                      </div>
                                    )}
                                  </div>

                                  {chargeTypesError && (
                                    <div role="alert" className="mb-3 rounded border border-red-200 bg-white p-3 text-sm text-red-700">
                                      {chargeTypesError} <button type="button" onClick={loadAccountingConfig} disabled={loadingChargeTypes} className="underline">Retry charge types</button>
                                    </div>
                                  )}
                                  {!loadingChargeTypes && !chargeTypesError && !getExtraCostHeads().length && (
                                    <p className="mb-3 text-sm">No active extra charge types configured. <a className="underline" href="/ecosystem/catalog/cost-ledger-config/cost-heads">Configure cost heads</a></p>
                                  )}
                                  {chargeError && (
                                    <div role="alert" className="mb-3 rounded border border-red-200 bg-white p-3 text-sm text-red-700">
                                      {chargeError} <button type="button" onClick={refreshPurchaseItemCosts} className="underline" disabled={loadingPurchaseItemCosts}>Retry loading charges</button>
                                    </div>
                                  )}
                                  {loadingPurchaseItemCosts && (
                                    <div className="mb-3 flex items-center justify-center gap-2 rounded border border-emerald-200 bg-white px-3 py-4 text-sm font-semibold text-emerald-800">
                                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-700" />
                                      Loading extra charges...
                                    </div>
                                  )}

                                  {savedPurchaseCosts.length > 0 && (
                                    <div className="purchase-table-scroll mb-3 min-w-0 rounded border bg-white">
                                      <table className="purchase-responsive-table w-full text-sm">
                                        <thead className="bg-emerald-100">
                                          <tr>
                                            <th className="p-2 text-left">Type</th>
                                            <th className="p-2 text-left">Item</th>
                                            <th className="p-2 text-right">Cost</th>
                                            <th className="p-2 text-right">GST</th>
                                            <th className="p-2 text-right">Total</th>
                                            <th className="p-2 text-left">Notes</th>
                                            <th className="p-2 text-left">Action</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {savedPurchaseCosts.map((cost) => {
                                            const costHead = getCostHead(cost.cost_head_id);
                                            const costItem = displayItems.find(
                                              (item) =>
                                                String(item.id) ===
                                                String(cost.purchase_order_item_id)
                                            );

                                            return (
                                              <tr key={cost.id} className="border-t">
                                                <td data-label="Type" className="p-2">
                                                  {costHead?.cost_head_name || 'Extra Cost'}
                                                </td>
                                                <td data-label="Item" className="p-2">
                                                  {costItem
                                                    ? getProductDisplayName(costItem)
                                                    : cost.purchase_order_item_id || '-'}
                                                </td>
                                                <td data-label="Cost" className="p-2 text-right">
                                                  ₹{Number(cost.base_amount || 0).toFixed(2)}
                                                </td>
                                                <td data-label="GST" className="p-2 text-right">
                                                  ₹{Number(cost.gst_amount || 0).toFixed(2)}
                                                </td>
                                                <td data-label="Total" className="p-2 text-right font-semibold">
                                                  ₹{Number(cost.total_amount || 0).toFixed(2)}
                                                </td>
                                                <td data-label="Notes" className="p-2">{cost.notes || '-'}</td>
                                                <td data-label="Action" className="p-2">{rowStatus === 'received' && <button type="button" onClick={() => removeExtraCharge(cost)} disabled={Boolean(removingChargeId) || isSavingTransportCharge || isSavingVerify || loadingChargeTypes || Boolean(chargeTypesError) || loadingPurchaseItemCosts} className="text-red-700 underline disabled:opacity-50">{removingChargeId === cost.id ? 'Removing...' : 'Remove'}</button>}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {rowStatus === 'received' && <div className="grid grid-cols-1 gap-2 md:grid-cols-7">
                                    <select
                                      aria-label="Charge type"
                                      disabled={loadingChargeTypes}
                                      value={transportDraft.cost_head_id}
                                      onChange={(e) =>
                                        updateTransportChargeDraft(
                                          po.id,
                                          'cost_head_id',
                                          e.target.value
                                        )
                                      }
                                      className="rounded border px-2 py-2 text-sm"
                                    >
                                      <option value="">{loadingChargeTypes ? 'Loading charge types...' : 'Select type'}</option>
                                      {getExtraCostHeads().map((head) => (
                                        <option key={head.id} value={head.id}>
                                          {head.cost_head_name || head.cost_head_code}
                                        </option>
                                      ))}
                                    </select>

                                    <select
                                      value={transportDraft.purchase_order_item_id}
                                      onChange={(e) =>
                                        updateTransportChargeDraft(
                                          po.id,
                                          'purchase_order_item_id',
                                          e.target.value
                                        )
                                      }
                                      className="rounded border px-2 py-2 text-sm md:col-span-2"
                                    >
                                      <option value="__all__">
                                        All Items (allocate by value)
                                      </option>
                                      {displayItems.map((item) => (
                                        <option key={item.id} value={item.id}>
                                          {getProductDisplayName(item)}
                                        </option>
                                      ))}
                                    </select>

                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={transportDraft.base_amount}
                                      onChange={(e) =>
                                        updateTransportChargeDraft(
                                          po.id,
                                          'base_amount',
                                          e.target.value
                                        )
                                      }
                                      className="rounded border px-2 py-2 text-sm text-right"
                                      placeholder="Cost"
                                    />

                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={transportDraft.gst_amount}
                                      onChange={(e) =>
                                        updateTransportChargeDraft(
                                          po.id,
                                          'gst_amount',
                                          e.target.value
                                        )
                                      }
                                      className="rounded border px-2 py-2 text-sm text-right"
                                      placeholder="GST"
                                    />

                                    <input
                                      value={transportDraft.notes}
                                      onChange={(e) =>
                                        updateTransportChargeDraft(
                                          po.id,
                                          'notes',
                                          e.target.value
                                        )
                                      }
                                      className="rounded border px-2 py-2 text-sm"
                                      placeholder="Notes"
                                    />

                                    <button
                                      type="button"
                                      onClick={() =>
                                        saveTransportCharge(po, displayItems)
                                      }
                                      disabled={
                                        isSavingTransportCharge || isSavingVerify ||
                                        !getExtraCostHeads().length
                                      }
                                      className="rounded bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:bg-gray-400"
                                    >
                                      {isSavingTransportCharge
                                        ? 'Saving...'
                                        : 'Add Charge'}
                                    </button>
                                  </div>}
                                </div>
                              )}

                              <div className="purchase-product-details max-w-full min-w-0" role="region" aria-label="Purchase order items" tabIndex={0}>
                              <table className={`purchase-responsive-table purchase-items-table w-full text-sm ${isVerifying ? 'is-verifying' : ''}`}>
                                <thead className="bg-gray-100">
                                  <tr>
                                    {isVerifying && (
                                      <th className="purchase-sticky-check p-2 text-center w-[90px]">
                                        <label className="inline-flex flex-col items-center gap-1">
                                          Confirmed
                                          <input
                                            type="checkbox"
                                            aria-label="Confirm all items"
                                            checked={verifyItems.length > 0 && verifyItems.every((i) => i.is_verified)}
                                            onChange={(e) => setAllVerified(e.target.checked)}
                                          />
                                        </label>
                                      </th>
                                    )}
                                    <th className="purchase-sticky-product p-2 text-left w-[330px]">
                                      Product
                                    </th>
                                    <th className="p-2 text-left w-[140px]">
                                      Category
                                    </th>
                                    <th className="p-2 text-left w-[130px]">
                                      Brand
                                    </th>
                                    <th className="p-2 text-right w-[100px]">
                                      Qty
                                    </th>
                                    <th className="p-2 text-left w-[90px]">
                                      Unit
                                    </th>
                                    <th className="p-2 text-right w-[120px]">
                                      No. Units
                                    </th>
                                    <th className="p-2 text-right w-[150px]">
                                      Expected Price
                                    </th>
                                    <th className="p-2 text-right">Actual Price</th>
                                    <th className="p-2 text-right w-[130px]">
                                      Extra Charges
                                    </th>
                                    <th className="p-2 text-right w-[130px]">
                                      Total
                                    </th>
                                    <th className="p-2 text-right w-[140px]">
                                      Final Unit Price
                                    </th>

                                    {isVerifying && (
                                      <>
                                        <th className="p-2 text-right">Unit MRP *</th>
                                        <th className="p-2 text-left">MFG Date</th>
                                        <th className="p-2 text-left">Expiry Date *</th>
                                        <th className="p-2 text-left">SKU</th>
                                        <th className="p-2 text-left w-[220px]">
                                          Product Remarks
                                        </th>
                                        <th className="p-2 text-center w-[90px]">
                                          Delete
                                        </th>
                                      </>
                                    )}

                                    {isEditingItems && (
                                      <th className="p-2 text-center w-[100px]">
                                        Remove
                                      </th>
                                    )}
                                  </tr>
                                </thead>

                                <tbody>
                                  {activeItems.map((item, index) => {
                                    const effectivePrice =
                                      getEffectivePrice(item);
                                    const total =
                                      Number(item.no_of_units || 1) *
                                      effectivePrice;
                                    const extraCost = getSavedItemExtraCost(po, item);
                                    const finalCost = total + extraCost;
                                    const finalCostPerPack =
                                      Number(item.no_of_units || 0) > 0
                                        ? Math.ceil(finalCost / Number(item.no_of_units || 1))
                                        : finalCost;

                                    const verifiedClass =
                                      isVerifying && item.is_verified
                                        ? 'bg-green-100 border-green-400'
                                        : isVerifying
                                        ? 'bg-red-50'
                                        : '';

                                    const selectedProductBarcodeId =
                                      item.product_barcode_id ||
                                      getProductBarcodeId(item);

                                    const missingFields =
                                      isVerifying && (showVerifyMissing || item.is_verified)
                                        ? getMissingVerifyFields(item)
                                        : [];
                                    const editCell = (field, label, display, extra = {}) => (
                                      <InlineEditCell
                                        label={label}
                                        editing={inlineEdit?.index === index && inlineEdit?.field === field}
                                        value={field === 'unit_mrp' ? getItemMrp(item) : item[field]}
                                        display={display}
                                        invalid={missingFields.includes(field)}
                                        onStart={() => setInlineEdit({ index, field })}
                                        onDone={(save, draft, move) => {
                                          if (save) commitInlineEdit(index, field, draft);
                                          moveInlineEdit(index, field, move);
                                        }}
                                        {...extra}
                                      />
                                    );
                                    const money = (v) =>
                                      v === null || v === undefined || v === ''
                                        ? <span className="text-gray-400">—</span>
                                        : `₹${Number(v).toFixed(2)}`;
                                    const textOrDash = (v) =>
                                      v ? v : <span className="text-gray-400">—</span>;

                                    return (
                                      <tr
                                        key={item.id || index}
                                        ref={(el) => {
                                          rowRefs.current[index] = el;
                                        }}
                                        tabIndex={-1}
                                        className={`border-t ${verifiedClass}`}
                                      >
                                        {isVerifying && (
                                          <td data-label="Confirmed" className="purchase-sticky-check p-2 text-center">
                                            <input
                                              type="checkbox"
                                              checked={Boolean(item.is_verified)}
                                              onChange={() =>
                                                toggleVerifyItem(index)
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            />
                                            <button
                                              type="button"
                                              disabled={isSavingVerify}
                                              className="mt-2 block text-xs text-emerald-700 underline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setItemVerifyModal({ open: true, index, item });
                                              }}
                                            >
                                              Item Details
                                            </button>
                                          </td>
                                        )}

                                        <td data-label="Product" className="purchase-sticky-product purchase-wrap p-2">
                                          {isVerifying && item.is_new ? (
                                            <select
                                              value={
                                                item.product_barcode_id || ''
                                              }
                                              onChange={(e) =>
                                                updateVerifyProduct(
                                                  index,
                                                  e.target.value
                                                )
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              className="border rounded px-2 py-1 w-full"
                                            >
                                              {item.catalogSelectionMissing && <option value="">{item.product_name} — no matching catalog barcode</option>}
                                              {productBarcodes.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                  {p.product_name_eng ||
                                                    p.product_name_tel ||
                                                    p.product_code}
                                                </option>
                                              ))}
                                            </select>
                                          ) : isEditingItems ? (
                                            <select
                                              value={selectedProductBarcodeId}
                                              onChange={(e) =>
                                                updateEditedProduct(
                                                  index,
                                                  e.target.value
                                                )
                                              }
                                              className="border rounded px-2 py-1 w-full"
                                            >
                                              {!selectedProductBarcodeId && (
                                                <option value="" disabled>
                                                  {item.product_name ||
                                                    'Product not matched'}
                                                </option>
                                              )}

                                              {productBarcodes.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                  {p.product_name_eng ||
                                                    p.product_name_tel ||
                                                    p.product_code}
                                                </option>
                                              ))}
                                            </select>
                                          ) : (
                                            <>
                                              <div className="font-medium">
                                                {getProductDisplayName(item)}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                Code: {item.product_code || '-'}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                MK:{' '}
                                                {normalizeBarcode(
                                                  item.mk_barcode
                                                ) || '-'}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                Vendor:{' '}
                                                {normalizeBarcode(
                                                  item.barcode
                                                ) || '-'}
                                              </div>
                                            </>
                                          )}
                                        </td>

                                        <td data-label="Category" className="p-2">
                                          {isVerifying && item.is_new ? renderVerifyCatalogSelect(item, index, 'category_id', 'Category') : item.category_name ||
                                            item.category_id ||
                                            '-'}
                                        </td>

                                        <td data-label="Brand" className="p-2">
                                          {isVerifying && item.is_new ? renderVerifyCatalogSelect(item, index, 'brand_id', 'Brand') : item.brand_name ||
                                            item.brand_id ||
                                            '-'}
                                        </td>

                                        {isVerifying ? editCell('qty', 'Qty', Number(item.qty || 0).toFixed(3), { type: 'number', min: '0.001', step: '0.001', className: 'text-right' }) : (
                                        <td data-label="Qty" className="p-2 text-right">
                                          {isEditingItems ? (
                                            <input
                                              type="number"
                                              min="0.001"
                                              step="0.001"
                                              value={item.qty}
                                              onChange={(e) =>
                                                updateEditedItem(
                                                      index,
                                                      'qty',
                                                      e.target.value
                                                    )
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              className="border rounded px-2 py-1 w-full text-right"
                                            />
                                          ) : (
                                            Number(item.qty || 0).toFixed(3)
                                          )}
                                        </td>
                                        )}

                                        <td data-label="Unit" className="p-2">
                                          {isVerifying && item.is_new ? renderVerifyCatalogSelect(item, index, 'unit_id', 'Unit') : item.unit_name ||
                                            item.unit_short_code ||
                                            item.unit_id ||
                                            '-'}
                                        </td>

                                        {isVerifying ? editCell('no_of_units', 'No. Units', Number(item.no_of_units || 1).toFixed(0), { type: 'number', min: '1', step: '1', className: 'text-right' }) : (
                                        <td data-label="No. Units" className="p-2 text-right">
                                          {isEditingItems ? (
                                            <input
                                              type="number"
                                              min="1"
                                              step="1"
                                              value={item.no_of_units}
                                              onChange={(e) =>
                                                updateEditedItem(
                                                      index,
                                                      'no_of_units',
                                                      e.target.value
                                                    )
                                              }
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              className="border rounded px-2 py-1 w-full text-right"
                                            />
                                          ) : (
                                            Number(
                                              item.no_of_units || 1
                                            ).toFixed(0)
                                          )}
                                        </td>
                                        )}

                                        <td data-label="Expected Price" className="p-2 text-right">
                                          {isEditingItems ? (
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              value={item.expected_unit_price}
                                              onChange={(e) =>
                                                updateEditedItem(
                                                  index,
                                                  'expected_unit_price',
                                                  e.target.value
                                                )
                                              }
                                              className="border rounded px-2 py-1 w-full text-right"
                                            />
                                          ) : (
                                            `₹${Number(
                                              item.expected_unit_price || 0
                                            ).toFixed(2)}`
                                          )}
                                        </td>

                                        {isVerifying ? editCell('actual_unit_price', 'Actual Price', (
                                          <span className={Number(item.actual_unit_price) !== Number(item.expected_unit_price || 0) ? 'font-semibold text-amber-700' : ''}>
                                            {money(item.actual_unit_price)}
                                          </span>
                                        ), { type: 'number', min: '0', step: '0.01', className: 'text-right' }) : (
                                        <td data-label="Actual Price" className="p-2 text-right">
                                          {item.actual_unit_price === null || item.actual_unit_price === undefined || item.actual_unit_price === ''
                                            ? '—' : `₹${Number(item.actual_unit_price).toFixed(2)}`}
                                        </td>
                                        )}
                                        <td data-label="Extra Charges" className="p-2 text-right text-blue-700">
                                          ₹{extraCost.toFixed(2)}
                                        </td>

                                        <td data-label="Total" className="p-2 text-right font-semibold">
                                          ₹{finalCost.toFixed(2)}
                                        </td>

                                        <td data-label="Final Unit Price" className="p-2 text-right font-semibold text-blue-700">
                                          ₹{Number(finalCostPerPack).toFixed(2)}
                                        </td>

                                        {isVerifying && (
                                          <>
                                            {editCell('unit_mrp', 'Unit MRP', money(getItemMrp(item)), { type: 'number', min: '0.01', step: '0.01', className: 'text-right' })}
                                            {editCell('mfg_date', 'MFG Date', textOrDash(item.mfg_date), { type: 'date' })}
                                            {editCell('exp_date', 'Expiry Date', textOrDash(item.exp_date), { type: 'date' })}
                                            {editCell('sku_id', 'SKU', item.sku_id || <span className="text-gray-400" title="Auto SKU used when blank">{makeSkuId(item)}</span>, { placeholder: makeSkuId(item) })}
                                            {editCell('inventory_remarks', 'Product Remarks', textOrDash(item.inventory_remarks), { placeholder: 'Product-wise remarks', className: 'purchase-wrap' })}

                                            <td data-label="Delete" className="p-2 text-center">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  removeVerifyProduct(index);
                                                }}
                                                className="text-red-600 hover:underline"
                                              >
                                                Delete
                                              </button>
                                            </td>
                                          </>
                                        )}

                                        {isEditingItems && (
                                          <td data-label="Remove" className="p-2 text-center">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                removeEditedItem(index)
                                              }
                                              className="text-red-600 hover:underline"
                                            >
                                              Remove
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="border-t-2 border-emerald-300 bg-emerald-100 font-bold">
                                  <tr>
                                    <th scope="row" colSpan={8 + (isVerifying ? 1 : 0)} className="p-3 text-left">Grand Total</th>
                                    <td data-label="Total Extra Charges" className="p-3 text-right text-blue-700">
                                      ₹{(activeItems.reduce((sum, item) => sum + Math.round(getSavedItemExtraCost(po, item) * 100), 0) / 100).toFixed(2)}
                                    </td>
                                    <td data-label="Grand Total" className="p-3 text-right">
                                      ₹{(activeItems.reduce((sum, item) => sum + Math.round((Number(item.no_of_units || 1) * getEffectivePrice(item) + getSavedItemExtraCost(po, item)) * 100), 0) / 100).toFixed(2)}
                                    </td>
                                    <td colSpan={1 + (isVerifying ? 6 : 0) + (isEditingItems ? 1 : 0)} />
                                  </tr>
                                </tfoot>
                              </table>
                              </div>

                              {isEditingItems && (
                                <div className="flex flex-wrap items-center justify-end gap-2 mt-3">
                                  <PurchaseProductSearch products={productBarcodes} value={newEditProductId} onChange={setNewEditProductId} />
                                  <button
                                    type="button"
                                    disabled={!newEditProductId}
                                    onClick={() => {
                                      updateEditedProduct(editedItems.length, newEditProductId);
                                      setNewEditProductId('');
                                    }}
                                    className="bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
                                  >
                                    Add Product
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => saveItems(po)}
                                    className="bg-green-700 text-white px-4 py-2 rounded"
                                  >
                                    Save Items
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEditItems}
                                    className="border px-4 py-2 rounded"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}

                              {isVerifying && (
                                <div className="flex justify-end gap-2 mt-3">
                                  <button
                                    type="button"
                                    onClick={() => saveVerify(po)}
                                    disabled={isSavingVerify || savingTransportChargeId === po.id}
                                    className="bg-emerald-700 text-white px-4 py-2 rounded disabled:cursor-not-allowed disabled:bg-emerald-400"
                                  >
                                    {isSavingVerify ? (
                                      <span className="inline-flex items-center gap-2">
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        {savingVerifyProgress || 'Verifying...'}
                                      </span>
                                    ) : (
                                      'Approve & Verify'
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelVerify}
                                    disabled={isSavingVerify || savingTransportChargeId === po.id}
                                    className="border px-4 py-2 rounded disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <VerifyItemInventoryModal
        token={token}
        receiptContext={(() => {const po=purchaseOrders.find(p=>p.id===verifyingId);return po?{purchase_order_id:po.id,warehouse_id:po.warehouse_id,supplier_id:po.supplier_id||po.stakeholders_id,stakeholders_id:po.stakeholders_id||po.supplier_id}:null;})()}
        open={itemVerifyModal.open}
        item={itemVerifyModal.item}
        appliedExtraCharge={getSavedItemExtraCost({ id: verifyingId }, itemVerifyModal.item)}
        onClose={() =>
          setItemVerifyModal({
            open: false,
            index: null,
            item: null,
          })
        }
        onSave={saveItemInventoryDetails}
      />
    </section>
  );
};

export default PurchaseOrdersStatusSections;
